/**
 * The lots an Inventory record holds: a table on the record, and a chooser to add one.
 *
 * ED, 2026-10-30: "Inventory is a collection of n lots ... if a user can see an Inventory record, they
 * must also have visibility to the Lot record to see it. When a contained lot is not visible to the
 * user in the Inventory record, some indication should be given to the user that the lot is not
 * visible to him, perhaps painted in grey and not clickable." And: "The lots in an inventory record,
 * must all have the same item."
 *
 * WHAT IT IS. Edges of the 'Inventory Lot' relationship type (parent the inventory's logical object,
 * child the lot's), read by /t1/v1/inventory_lots, which checks EACH lot under the reader's own
 * persona: a lot the reader may see comes with its name, lot status, quantity on hand, unit and
 * location and links to its page; a lot they may not comes as its ID and nothing else, drawn grey
 * with "not visible to you", and is not a link. The totals above the table are the server's sum over
 * the visible lots only, and the count of hidden ones is said beside them.
 *
 * ADDING goes through the same relationship door the Related Objects grid uses; the server refuses
 * a lot of another item (CC-INVL-MISMATCH), a lot of nothing (CC-INVL-LOTOF) and an inventory that
 * names no item yet (CC-INVL-ITEM), and the refusal is shown as the server phrased it. The chooser
 * is the Lots list screen in picker mode, the same idiom as the contract's purchase orders.
 */
import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Blue3, Grey2, Grey6, Grey7, Grey8, Grey17} from "../../theme/styles";
import {OBJECT_CLASSIFICATIONS} from "../../util/objectClassifications";
import ObjectList, {PickedRecord} from "../../pages/ObjectList";

const EDGE_TYPE = "Inventory Lot";
const RELATIONSHIP_IDOC = "v1.ObjectRelationship-001";

type Row = {EdgeUSID: string; ObjectUSID: string; RevisionUSID?: string; ObjectID?: string | null; Name?: string | null;
            Status?: string | null; Visible: boolean; LotStatus?: string | null; QuantityOnHand?: string | null;
            QuantityUnit?: string | null; Location?: string | null; NextCountDue?: string | null};
type Summary = {Item?: {ObjectUSID: string; ObjectID?: string | null; Name?: string | null} | null;
                VisibleCount?: number; HiddenCount?: number; QuantityOnHand?: string | null;
                QuantityUnit?: string | null; MixedUnits?: boolean; Converted?: boolean; Dimension?: string | null};

function listOf<T>(held: T[] | T | undefined | null): T[] {
    if (held === null || held === undefined) return [];
    return Array.isArray(held) ? held : [held];
}

function qbe(idoc: string, component: string, members: {Name: string; Value: any; Operator: string}[], limit: number) {
    return btoa(JSON.stringify({PlatformMessage: {"-idoc": idoc, [component]: {
        _attributes: {limit, sort: null, offset: 0},
        QueryGroups: {QueryGroup: [{Name: "primary", Operator: "AND", Type: "QueryGroup",
                                    Membership: {Member: members.map(m => ({Type: "Attribute", ...m}))}}]}}}}));
}

export default function InventoryLotsPane({objectUSID, status, canEdit}: {
    /** The inventory's LOGICAL object. Edges hang off the object, never a revision. */
    objectUSID: string;
    status?: string | null;
    /** Whether the reader holds the inventory's edit on this record; the add and remove controls
     *  are hidden without it (the server refuses regardless). */
    canEdit?: boolean;
}) {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);
    const navigate = useNavigate();

    const [typeUSID, setTypeUSID] = useState<string | null>(null);
    const [rows, setRows] = useState<Row[] | null>(null);
    const [summary, setSummary] = useState<Summary>({});
    const [problem, setProblem] = useState<string | null>(null);
    const [adding, setAdding] = useState(false);
    const [removing, setRemoving] = useState<string | null>(null);
    const frozen = status != null && !["DRAFT", "RELEASED"].includes(String(status).toUpperCase());

    useEffect(() => {
        let cancelled = false;
        api.get("t1/v1/object_relationship_types", {
            params: {QBE: qbe("v1.ObjectRelationshipType-001", "ObjectRelationshipType",
                               [{Name: "Name", Value: EDGE_TYPE, Operator: "EQUAL"}], 1)},
        }).then((response: any) => {
            if (cancelled) return;
            const found = listOf<any>(response?.data?.PlatformMessage?.ListOfObjectRelationshipType?.ObjectRelationshipType);
            setTypeUSID(found[0]?.USID ?? null);
        }).catch(() => { if (!cancelled) setTypeUSID(null); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = useCallback(() => {
        if (!objectUSID) return;
        api.get("t1/v1/inventory_lots", {params: {InventoryObjectUSID: objectUSID}})
            .then((response: any) => {
                const message = response?.data?.PlatformMessage ?? {};
                setRows(listOf<Row>(message?.ListOfInventoryLot?.InventoryLot));
                setSummary({Item: message.Item, VisibleCount: message.VisibleCount, HiddenCount: message.HiddenCount,
                            QuantityOnHand: message.QuantityOnHand, QuantityUnit: message.QuantityUnit, MixedUnits: message.MixedUnits,
                            Converted: message.Converted, Dimension: message.Dimension});
                setProblem(null);
            })
            .catch((failure: any) => {
                setRows([]);
                setProblem(failure?.response?.data?.Cause ?? "The lots could not be read.");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectUSID]);

    useEffect(() => { load(); }, [load]);

    const remove = (row: Row) => {
        setRemoving(row.EdgeUSID);
        api.delete("t1/v1/object_relationships", {
            headers: {"idoc-request": RELATIONSHIP_IDOC},
            data: {PlatformMessage: {"-idoc": RELATIONSHIP_IDOC, ObjectRelationship: {USID: row.EdgeUSID}}},
        }).then(() => load())
          .catch((failure: any) => setProblem(failure?.response?.data?.Cause ?? "The lot could not be removed."))
          .finally(() => setRemoving(null));
    };

    const add = (lot: PickedRecord) => {
        if (!typeUSID) return Promise.reject(new Error("no type"));
        return api.post("t1/v1/object_relationships",
            {PlatformMessage: {"-idoc": RELATIONSHIP_IDOC, ObjectRelationship: {
                ParentUSID: objectUSID, ChildUSID: lot.ObjectUSID, ObjectRelationshipTypeUSID: typeUSID}}},
            {headers: {"idoc-request": RELATIONSHIP_IDOC, "idoc-response": RELATIONSHIP_IDOC}});
    };

    const already = useMemo(() => new Set((rows ?? []).map(r => String(r.ObjectUSID))), [rows]);
    const hidden = summary.HiddenCount ?? 0;

    return (
        <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", padding: "12px 14px"}}>
            <Box sx={{display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px"}}>
                <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8}}>
                    Lots{rows ? ` (${rows.length})` : ""}
                </Typography>
                {summary.Item ? (
                    <Typography sx={{fontSize: 13, color: Grey7}}>
                        of {summary.Item.ObjectID ?? ""}{summary.Item.Name ? ` ${summary.Item.Name}` : ""}
                    </Typography>
                ) : null}
                <Box sx={{flex: 1}}/>
                {canEdit !== false ? (
                    <Button size="small" variant="outlined" sx={{textTransform: "none"}}
                            disabled={!typeUSID || frozen} onClick={() => setAdding(true)}
                            title={frozen ? "A finished record holds what it held." : undefined}>
                        Add lot
                    </Button>
                ) : null}
            </Box>

            {/* THE TOTALS, over what the reader can see, with the hidden count beside them so a
                number that leaves lots out never reads as the whole. */}
            {rows && rows.length > 0 ? (
                <Typography sx={{fontSize: 13, color: Grey7, marginBottom: "10px"}}>
                    {summary.MixedUnits && !summary.Converted
                        ? "On hand: the visible lots are in more than one unit and not all of them convert, so there is no one figure."
                        : `On hand across the lots you can see: ${summary.QuantityOnHand ?? "0"}${summary.QuantityUnit ? ` ${summary.QuantityUnit}` : ""}${summary.Converted ? `, the lots' units converted to ${summary.Dimension ? `the ${summary.Dimension.toLowerCase()} base unit` : "the base unit"}` : ""}.`}
                    {hidden > 0 ? ` ${hidden} ${hidden === 1 ? "lot is" : "lots are"} not visible to you and not counted.` : ""}
                </Typography>
            ) : null}

            {problem && <Typography sx={{fontSize: 13, color: "#B3261E", marginBottom: "8px"}}>{problem}</Typography>}
            {rows === null && <Typography sx={{fontSize: 13, color: Grey7}}>Reading…</Typography>}
            {rows && rows.length === 0 && (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6}}>
                    No lots are held in this inventory yet.
                    {summary.Item ? "" : " Choose the item on the Form tab before adding one: every lot held is a lot of that item."}
                </Typography>
            )}
            {rows && rows.length > 0 && (
                <Box sx={{display: "grid", gridTemplateColumns: "1.1fr 2.4fr 1fr 1fr 1.4fr auto", gap: "8px 12px",
                          alignItems: "center"}}>
                    {["ID", "Name", "Lot status", "On hand", "Location", ""].map((head, index) => (
                        <Typography key={`${head}${index}`}
                                    sx={{fontSize: 11.5, color: Grey7, textTransform: "uppercase", letterSpacing: "0.04em"}}>
                            {head}
                        </Typography>
                    ))}
                    {rows.map(row => row.Visible ? (
                        <React.Fragment key={row.EdgeUSID}>
                            <Typography sx={{fontSize: 13, color: Blue3, cursor: "pointer"}}
                                        onClick={() => row.RevisionUSID && navigate(`/object/${row.RevisionUSID}`)}>
                                {row.ObjectID ?? "—"}
                            </Typography>
                            <Typography sx={{fontSize: 13, color: Grey17}}>{row.Name ?? "—"}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.LotStatus ?? "—"}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>
                                {row.QuantityOnHand ?? "—"}{row.QuantityUnit ? ` ${row.QuantityUnit}` : ""}
                            </Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.Location ?? "—"}</Typography>
                            {canEdit !== false && !frozen ? (
                                <Typography sx={{fontSize: 12.5, color: Grey7, cursor: "pointer"}}
                                            onClick={() => removing !== row.EdgeUSID && remove(row)}>
                                    {removing === row.EdgeUSID ? "Removing…" : "Remove"}
                                </Typography>
                            ) : <span/>}
                        </React.Fragment>
                    ) : (
                        // NOT VISIBLE TO THIS READER: the ID in grey, no link, and the reason said. The
                        // server sent nothing else about it, so there is nothing else to draw.
                        <React.Fragment key={row.EdgeUSID}>
                            <Typography sx={{fontSize: 13, color: Grey6}} title="This lot is not visible to you.">
                                {row.ObjectID ?? "—"}
                            </Typography>
                            <Typography sx={{fontSize: 13, color: Grey6, fontStyle: "italic", gridColumn: "span 5"}}>
                                Not visible to you
                            </Typography>
                        </React.Fragment>
                    ))}
                </Box>
            )}
            {adding && (
                <LotChooser already={already} onClose={() => setAdding(false)} onChoose={add}
                            onDone={() => { setAdding(false); load(); }}/>
            )}
        </Box>
    );
}

/** The chooser: the Lots list screen, contained in a dialog, several at once. */
function LotChooser({already, onClose, onChoose, onDone}: {
    already: Set<string>; onClose: () => void; onChoose: (lot: PickedRecord) => Promise<any>; onDone: () => void;
}) {
    const [problem, setProblem] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [ticked, setTicked] = useState<PickedRecord[]>([]);
    const classification = OBJECT_CLASSIFICATIONS.find(one => one.code === "Lot");
    if (!classification) return null;

    const select = () => {
        const fresh = ticked.filter(one => one.ObjectUSID && !already.has(String(one.ObjectUSID)));
        if (fresh.length === 0) {
            setProblem(ticked.length ? "Every lot ticked is already held here." : "Nothing is ticked.");
            return;
        }
        setBusy(true);
        // ONE AT A TIME, so a refusal names the lot it is about and the lots before it stay added.
        fresh.reduce((chain, one) => chain.then(() => onChoose(one)), Promise.resolve())
            .then(() => onDone())
            .catch((failure: any) => {
                setProblem(failure?.response?.data?.Cause ?? "A lot could not be added.");
                setBusy(false);
            });
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xl"
                slotProps={{paper: {sx: {height: "90vh", maxHeight: "90vh", display: "flex", flexDirection: "column"}}}}>
            <DialogTitle sx={{fontSize: 16, paddingBottom: "6px", flex: "0 0 auto"}}>
                Add lots
                {busy ? <span style={{fontSize: 13, color: Grey7, marginLeft: 12}}>Adding…</span> : null}
            </DialogTitle>
            {problem && (
                <Typography sx={{fontSize: 13, color: "#B3261E", padding: "0 24px 6px", flex: "0 0 auto"}}>{problem}</Typography>
            )}
            <DialogContent sx={{padding: 0, flex: "1 1 auto", minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column"}}>
                <ObjectList classification={classification} picker={{multiple: true, onSelectionChange: setTicked}}/>
            </DialogContent>
            <DialogActions sx={{padding: "12px 24px", borderTop: "1px solid #E4E7EC", flex: "0 0 auto"}}>
                <Button onClick={onClose} sx={{textTransform: "none"}}>Cancel</Button>
                <Button variant="contained" disableElevation onClick={select} disabled={busy || ticked.length === 0}
                        sx={{textTransform: "none"}}>
                    Select{ticked.length > 0 ? ` (${ticked.length})` : ""}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
